/**
 * The animation data model for video overlays: a layer animates via a list of
 * `AnimationBlock`s, Jitter-style action blocks rather than keyframes. Each
 * block is one draggable timeline segment: (action, start, duration, easing,
 * params), plus a text-unit spec on text layers.
 *
 * A block is an operation on one property of the layer. It animates the
 * property from `from` to `to` over its duration and then holds `to`. Both
 * ends are optional: an absent `from` means "wherever the property is when
 * the block starts" (the layer's authored rest if nothing ran before, else
 * the previous block's end), and an absent `to` means the authored rest. So
 * an entrance is a block with a `from` offset, an exit is a block with a `to`
 * offset, and a change is a block with an explicit target; consecutive blocks
 * on one property chain by value. The first block on a property that carries
 * a `from` also sets the property before it starts, so an entrance's offset
 * applies from the layer's first frame. Nothing repeats: sustained motion is
 * `wiggle`, which oscillates for its duration.
 *
 * Values are relative to rest: scale 1, rotate 0°, move 0px, blur 0px,
 * mask-wipe fully revealed (1), stroke fully drawn (1); opacity is absolute
 * (rest 1).
 *
 * Defined here rather than in `ad-studio-renderer` because the Doc owns
 * motion (`layer.motion.blocks`, serialized as `data-ht-motion`) and lib
 * deliberately holds no React or Remotion dependency; the renderer evaluates
 * the blocks per frame. `parseAnimationBlocks` is the shared untrusted-JSON
 * boundary for both the Doc parser and the renderer.
 */

/** The composition version this build authors and renders. */
export const COMPOSITION_VERSION = 2 as const;

export type CompositionVersion = typeof COMPOSITION_VERSION;

/**
 * Rejects a composition this build cannot faithfully render. `version` comes
 * from untrusted composition JSON, so despite the field's compile-time type
 * anything other than the supported number throws. An absent version means 1,
 * the legacy descriptor format, so it throws too. The
 * alternative is rendering the ad without its animations and shipping that
 * silently.
 */
export function assertSupportedCompositionVersion(composition: {
  version?: CompositionVersion;
}): void {
  const version = composition.version ?? 1;
  if (version !== COMPOSITION_VERSION) {
    throw new Error(
      `Unsupported composition version ${JSON.stringify(version)}; this build renders version ${COMPOSITION_VERSION}. Refusing to render rather than dropping animations.`,
    );
  }
}

/** Canvas edge a layer travels from or a wipe reveals from. */
export type SlideEdge = "top" | "bottom" | "left" | "right";

export const SLIDE_EDGES: readonly SlideEdge[] = [
  "top",
  "bottom",
  "left",
  "right",
];

/**
 * Named easing curves. Every name except `linear` accepts an `intensity`
 * (0..100, default 50) that scales how pronounced the curve's character is.
 */
export const EASING_NAMES = [
  "smooth",
  "natural",
  "slow-down",
  "accelerate",
  "overshoot",
  "elastic",
  "bounce",
  "impulse",
  "swing",
  "linear",
] as const;

export type EasingName = (typeof EASING_NAMES)[number];

export interface Easing {
  name: EasingName;
  /** 0..100, default 50. Ignored by `linear`. */
  intensity?: number;
  /** Explicit cubic-bezier control points; overrides `name` when present. */
  bezier?: [number, number, number, number];
}

export type TextUnit = "letter" | "word" | "line";

const TEXT_UNITS: readonly TextUnit[] = ["letter", "word", "line"];

type TextUnitOrder = "forward" | "reverse" | "center-out" | "random";

export const TEXT_UNIT_ORDERS: readonly TextUnitOrder[] = [
  "forward",
  "reverse",
  "center-out",
  "random",
];

export interface TextUnitSpec {
  unit: TextUnit;
  order: TextUnitOrder;
  /** Frames between consecutive units. */
  delay: number;
  /** Redistributes the unit offsets along this curve. */
  smoothing?: Easing;
}

/** Where a mask-wipe reveal starts. */
export type WipeOrigin = SlideEdge | "center";

export const WIPE_ORIGINS: readonly WipeOrigin[] = [...SLIDE_EDGES, "center"];

/**
 * A position for a move: fully past a canvas edge (resolved against the
 * layer's measured bounds), or an offset from the layer's resting spot in px.
 * A missing axis is 0, the rest.
 */
export type MovePoint = { edge: SlideEdge } | { x?: number; y?: number };

/**
 * Per-action params, discriminated by `action` so evaluators can switch
 * exhaustively. `from`/`to` follow the block contract above; the remaining
 * params shape the action. Required params make a block meaningless when
 * absent (the parser rejects such blocks).
 */
type ActionSpec =
  | { action: "move"; params: { from?: MovePoint; to?: MovePoint } }
  | {
      action: "scale";
      params: { from?: number; to?: number; origin?: string };
    }
  | {
      action: "rotate";
      /** Degrees from the authored angle. */
      params: { from?: number; to?: number; origin?: string };
    }
  | { action: "opacity"; params: { from?: number; to?: number } }
  | { action: "blur"; params: { from?: number; to?: number } }
  | { action: "color"; params: { from?: string; to: string } }
  | {
      action: "mask-wipe";
      /** `from`/`to` are the revealed fraction (0 hidden, 1 shown); `edge`
       * is where the reveal grows from. */
      params: {
        from?: number;
        to?: number;
        edge?: WipeOrigin;
        shape?: "rect" | "circle";
      };
    }
  /** SVG layers only: draws the strokes of every shape inside the layer along
   * their length. `from`/`to` are the drawn fraction (0 none, 1 whole).
   * Shapes with a fill and no stroke show nothing. */
  | { action: "stroke"; params: { from?: number; to?: number } }
  /** Instant visibility toggle at `start`. A layer whose first toggle shows
   * it is hidden until then. */
  | { action: "hide-show"; params: { visible: boolean } }
  /** Animates the layer's box (width and/or height, px), center-anchored:
   * Jitter's non-uniform size primitive (it has no per-axis scale). Rest is
   * the layer's measured size, so resize needs geometry bounds. */
  | { action: "resize"; params: { width?: number; height?: number } }
  | { action: "corner-radius"; params: { from?: number; to?: number } }
  | {
      action: "counter";
      params: {
        from: number;
        to: number;
        decimals?: number;
        /** Literal text around the number, e.g. `prefix: "$"`. */
        prefix?: string;
        suffix?: string;
        /** Thousands separators (en-US), default true. */
        separator?: boolean;
      };
    }
  /** Transitions the text to `to` via a rolling character replace over the
   * block's duration; duration 0 swaps instantly at `start`. */
  | { action: "change-text"; params: { to: string } }
  /** Oscillates for the block's duration on its own clock. */
  | {
      action: "wiggle";
      params: {
        /** What oscillates: element position (default) or its scale. */
        property?: "position" | "scale";
        axis?: "x" | "y" | "both";
        /** Position: px amplitude. Scale: added scale amplitude (0.03 = 3%). */
        distance?: number;
        speed?: number;
      };
    };

export type ActionName = ActionSpec["action"];

export type AnimationBlock = ActionSpec & {
  id: string;
  /** Frames, relative to the layer's `from`. */
  start: number;
  /** Frames the block runs. */
  duration: number;
  /** Default: `{ name: "smooth" }`. */
  easing?: Easing;
  /** Text layers only: run the block per letter/word/line with stagger. */
  text?: TextUnitSpec;
  /** Set when the block came from expanding a named preset. */
  presetName?: string;
};

// --- Untrusted-JSON parsing ---

type Raw = Record<string, unknown>;

export const isRaw = (value: unknown): value is Raw =>
  value !== null && typeof value === "object" && !Array.isArray(value);

export const finite = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const oneOf = <T extends string>(
  value: unknown,
  values: readonly T[],
): value is T => values.includes(value as T);

const isBezier = (value: unknown): value is [number, number, number, number] =>
  Array.isArray(value) && value.length === 4 && value.every(finite);

const inUnitRange = (value: number): boolean => value >= 0 && value <= 1;

export function parseEasing(value: unknown): Easing | undefined {
  if (!isRaw(value)) return undefined;
  if (!oneOf(value.name, EASING_NAMES)) return undefined;
  const easing: Easing = { name: value.name };
  if (finite(value.intensity)) {
    easing.intensity = Math.min(100, Math.max(0, value.intensity));
  }
  const bezier = value.bezier;
  // Remotion's bezier throws mid-render when an x control point leaves [0, 1],
  // so such a curve falls back to the named easing.
  if (isBezier(bezier) && inUnitRange(bezier[0]) && inUnitRange(bezier[2])) {
    easing.bezier = bezier;
  }
  return easing;
}

function parseTextUnitSpec(value: unknown): TextUnitSpec | undefined {
  if (!isRaw(value)) return undefined;
  if (!oneOf(value.unit, TEXT_UNITS)) return undefined;
  if (!oneOf(value.order, TEXT_UNIT_ORDERS)) return undefined;
  if (!finite(value.delay) || value.delay < 0) return undefined;
  const spec: TextUnitSpec = {
    unit: value.unit,
    order: value.order,
    delay: value.delay,
  };
  const smoothing = parseEasing(value.smoothing);
  if (smoothing) spec.smoothing = smoothing;
  return spec;
}

/** A move point from untrusted JSON, or undefined when it names no travel. */
function parseMovePoint(value: unknown): MovePoint | undefined {
  if (!isRaw(value)) return undefined;
  if (oneOf(value.edge, SLIDE_EDGES)) return { edge: value.edge };
  const point: { x?: number; y?: number } = {};
  if (finite(value.x)) point.x = value.x;
  if (finite(value.y)) point.y = value.y;
  return Object.keys(point).length ? point : undefined;
}

/** Copies `from`/`to` when they are finite numbers. */
function numericEnds(params: Raw): { from?: number; to?: number } {
  const ends: { from?: number; to?: number } = {};
  if (finite(params.from)) ends.from = params.from;
  if (finite(params.to)) ends.to = params.to;
  return ends;
}

/**
 * Per-action param validation. Returns the typed spec, or null when a
 * required param is missing/malformed (the block would be meaningless).
 * Optional params are dropped when malformed rather than failing the block.
 */
function parseActionSpec(action: unknown, params: Raw): ActionSpec | null {
  switch (action) {
    case "move": {
      const spec: ActionSpec = { action, params: {} };
      const from = parseMovePoint(params.from);
      const to = parseMovePoint(params.to);
      if (from) spec.params.from = from;
      if (to) spec.params.to = to;
      // A move with no travel at all animates nothing.
      if (!from && !to) return null;
      return spec;
    }
    case "scale":
    case "rotate": {
      const origin =
        typeof params.origin === "string" ? { origin: params.origin } : {};
      return { action, params: { ...numericEnds(params), ...origin } };
    }
    case "opacity":
    case "blur":
    case "corner-radius":
    case "stroke":
      return { action, params: numericEnds(params) };
    case "color": {
      if (typeof params.to !== "string" || !params.to) return null;
      const spec: ActionSpec = { action, params: { to: params.to } };
      if (typeof params.from === "string" && params.from) {
        spec.params.from = params.from;
      }
      return spec;
    }
    case "mask-wipe": {
      const spec: ActionSpec = { action, params: numericEnds(params) };
      if (oneOf(params.edge, WIPE_ORIGINS)) spec.params.edge = params.edge;
      if (oneOf(params.shape, ["rect", "circle"] as const)) {
        spec.params.shape = params.shape;
      }
      return spec;
    }
    case "hide-show": {
      if (typeof params.visible !== "boolean") return null;
      return { action, params: { visible: params.visible } };
    }
    case "resize": {
      const spec: ActionSpec = { action, params: {} };
      if (finite(params.width)) spec.params.width = params.width;
      if (finite(params.height)) spec.params.height = params.height;
      if (spec.params.width === undefined && spec.params.height === undefined) {
        return null;
      }
      return spec;
    }
    case "counter": {
      if (!finite(params.from) || !finite(params.to)) return null;
      const spec: ActionSpec = {
        action,
        params: { from: params.from, to: params.to },
      };
      if (finite(params.decimals) && params.decimals >= 0) {
        spec.params.decimals = Math.floor(params.decimals);
      }
      if (typeof params.prefix === "string") spec.params.prefix = params.prefix;
      if (typeof params.suffix === "string") spec.params.suffix = params.suffix;
      if (typeof params.separator === "boolean") {
        spec.params.separator = params.separator;
      }
      return spec;
    }
    case "change-text": {
      if (typeof params.to !== "string") return null;
      return { action, params: { to: params.to } };
    }
    case "wiggle": {
      const spec: ActionSpec = { action, params: {} };
      if (oneOf(params.property, ["position", "scale"] as const)) {
        spec.params.property = params.property;
      }
      if (oneOf(params.axis, ["x", "y", "both"] as const)) {
        spec.params.axis = params.axis;
      }
      if (finite(params.distance)) spec.params.distance = params.distance;
      if (finite(params.speed) && params.speed > 0) {
        spec.params.speed = params.speed;
      }
      return spec;
    }
    default:
      return null;
  }
}

/** One block from untrusted JSON, or null when it cannot be evaluated. */
export function parseAnimationBlock(value: unknown): AnimationBlock | null {
  if (!isRaw(value)) return null;
  if (typeof value.id !== "string" || !value.id) return null;
  if (!finite(value.start) || !finite(value.duration)) return null;
  if (value.start < 0 || value.duration < 0) return null;
  const spec = parseActionSpec(
    value.action,
    isRaw(value.params) ? value.params : {},
  );
  if (!spec) return null;

  const block: AnimationBlock = {
    ...spec,
    id: value.id,
    start: value.start,
    duration: value.duration,
  };
  const easing = parseEasing(value.easing);
  if (easing) block.easing = easing;
  const text = parseTextUnitSpec(value.text);
  if (text) block.text = text;
  if (typeof value.presetName === "string") block.presetName = value.presetName;
  return block;
}

/**
 * A layer's block list from untrusted JSON. Malformed entries are skipped
 * rather than failing the list, so one corrupt block cannot take down the
 * whole ad.
 */
export function parseAnimationBlocks(value: unknown): AnimationBlock[] {
  if (!Array.isArray(value)) return [];
  return value
    .map(parseAnimationBlock)
    .filter((block): block is AnimationBlock => block !== null);
}
